import { defineComponent as _defineComponent } from 'vue'
import { createCommentVNode as _createCommentVNode } from "vue"

import { computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import i18n from '@/i18n'
import { session } from '@/composables/useSession'
import { hasAccountRole } from '@/utils/roleUtils'
import { hasZendeskProxy } from '@/config'
import type { SidebarConfig } from '../types'

type SettingsItemKey =
  // Profile section
  | 'profile.general'
  | 'profile.notifications'
  | 'profile.logins'
  | 'profile.templates'
  // Account section
  | 'settings.profile'
  | 'settings.connections'
  | 'settings.notifications'
  | 'settings.reviews'
  | 'settings.ai'
  | 'settings.survey'
  | 'settings.authentications'
  | 'settings.subscription'


export default /*@__PURE__*/_defineComponent({
  __name: 'SettingsSidebar',
  emits: ["update:config"],
  setup(__props, { emit: __emit }) {

const emit = __emit

const router = useRouter()
const route = useRoute()

const currentRouteKey = computed(() => route?.name?.toString() || '')

const handleSubnavItemClick = (navItem: string, subnavItem: string) => {
  if (navItem === 'settings') {
    router.push({ name: subnavItem as SettingsItemKey })
  }
}

const getPersonalSettingsItems = computed(() => {
  const items = [
    {
      children: i18n.t('profile.navigation.general'),
      key: 'profile.general',
      isCurrent: currentRouteKey.value === 'profile.general',
      dataTestId: 'profile-general',
      view: 'settings',
    },
    {
      children: i18n.t('profile.navigation.notifications'),
      key: 'profile.notifications',
      isCurrent: currentRouteKey.value === 'profile.notifications',
      dataTestId: 'profile-notifications',
      view: 'settings',
    },
  ]

  // Add logins if not using Zendesk proxy
  if (!hasZendeskProxy) {
    items.push({
      children: i18n.t('profile.navigation.logins'),
      key: 'profile.logins',
      isCurrent: currentRouteKey.value === 'profile.logins',
      dataTestId: 'profile-logins',
      view: 'settings',
    })
  }

  items.push({
    children: i18n.t('profile.navigation.templates'),
    key: 'profile.templates',
    isCurrent: currentRouteKey.value?.startsWith('profile.templates') || false,
    dataTestId: 'profile-templates',
    view: 'settings',
  })

  return items
})

const getAccountSettingsItems = computed(() => {
  if (!hasAccountRole('ADMIN', 'MANAGER')) {
    return []
  }

  const items = [
    {
      children: i18n.t('profile.navigation.general'),
      key: 'settings.profile',
      isCurrent: currentRouteKey.value === 'settings.profile',
      dataTestId: 'account-general-btn',
      view: 'settings',
    },
    {
      children: i18n.t('settings.navigation.connections'),
      key: 'settings.connections',
      isCurrent: currentRouteKey.value === 'settings.connections',
      dataTestId: 'account-connections-btn',
      view: 'settings',
    },
    {
      children: i18n.t('settings.navigation.notifications'),
      key: 'settings.notifications',
      isCurrent: currentRouteKey.value === 'settings.notifications',
      dataTestId: 'account-notifications-btn',
      view: 'settings',
    },
  ]

  // Scorecard items
  items.push({
    children: i18n.t('settings.navigation.scorecards'),
    key: 'settings.reviews',
    isCurrent: currentRouteKey.value?.startsWith('settings.reviews') || false,
    dataTestId: 'settings-reviews',
    view: 'settings',
  })

  // AI Insights
  items.push({
    children: i18n.t('settings.navigation.ai'),
    key: 'settings.ai',
    isCurrent: currentRouteKey.value?.startsWith('settings.ai') || false,
    dataTestId: 'settings-ai',
    view: 'settings',
  })

  // Surveys
  if (session.features.surveys) {
    items.push({
      children: i18n.t('universal.surveys'),
      key: 'settings.survey',
      isCurrent: currentRouteKey.value === 'settings.survey',
      dataTestId: 'settings-surveys',
      view: 'settings',
    })
  }

  // Authentications (only if not using Zendesk proxy)
  if (!hasZendeskProxy) {
    items.push({
      children: i18n.t('settings.navigation.logins'),
      key: 'settings.authentications',
      isCurrent: currentRouteKey.value === 'settings.authentications',
      dataTestId: 'settings-authentications',
      view: 'settings',
    })
  }

  return items
})

const getSubscriptionItem = computed(() => {
  // Subscription (only for ADMIN and not using Zendesk proxy)
  if (hasZendeskProxy || !hasAccountRole('ADMIN')) {
    return []
  }

  return [
    {
      children: i18n.t('settings.navigation.subscription'),
      key: 'settings.subscription',
      isCurrent: currentRouteKey.value === 'settings.subscription',
      dataTestId: 'settings-subscription',
      view: 'settings',
    },
  ]
})

const items = computed(() => {
  const itemsArray = []

  // Personal settings group
  itemsArray.push({
    key: 'personal-settings',
    legend: i18n.t('profile.navigation.title'),
    items: getPersonalSettingsItems.value,
    dataTestId: 'personal-settings-group',
    view: 'settings',
  })

  // Account settings group (only for ADMIN/MANAGER)
  const accountItems = getAccountSettingsItems.value
  if (accountItems.length > 0) {
    itemsArray.push({
      key: 'account-settings',
      legend: i18n.t('settings.navigation.title'),
      items: accountItems,
      dataTestId: 'account-settings-group',
      view: 'settings',
    })
  }

  // Subscription (only for ADMIN and not using Zendesk proxy)
  const subscriptionItems = getSubscriptionItem.value
  if (subscriptionItems.length > 0) {
    itemsArray.push(...subscriptionItems)
  }

  return itemsArray
})

const config = computed(
  (): SidebarConfig => ({
    action: {
      tooltip: '',
      menuItems: [],
      onClick: () => {
        /* no-op */
      },
    },
    title: i18n.t('settings.title'),
    items: items.value,
    handleSubnavItemClick,
    handleSubnavItemActionClick: () => {
      /* no-op */
    },
    handleGroupActionClick: () => {
      /* no-op */
    },
  }),
)

// Emit initial config and watch for changes
onMounted(() => {
  emit('update:config', config.value)
})

// Watch for changes and emit updates
watch(config, (newConfig) => {
  emit('update:config', newConfig)
})

return (_ctx: any,_cache: any) => {
  return _createCommentVNode(" Renderless component ")
}
}

})